import React, {useCallback} from 'react';
import {DialogOverlay, DialogContent} from '@reach/dialog';
import VisuallyHidden from '@reach/visually-hidden';
import {FormattedMessage, useIntl} from 'react-intl';
import {Link} from 'gatsby';
import {localeToPath} from '../../../utils/localeToPath';
import {LocaleEnum, getSupportedLocalesAndAlias, localeToLocaleAlias, type SlugLocale, UrlLocaleEnum} from '../../../types/locale';
import classNames from 'classnames';

type FooterLocaleSwitcherProps = {
  slugLocales: SlugLocale[];
};

export enum CountryIntlCodes {
  deAT = 'AT',
  deDE = 'DE',
  deCH = 'CH',
  enIE = 'IE',
  enUS = 'EU'
}

const FlagFileCodes = Object.fromEntries(Object.entries(CountryIntlCodes).map(([key, value]) => [key, value.toLowerCase()]));

export const supportedLocales = getSupportedLocalesAndAlias().locales;

export const FooterLocaleSwitcher: React.FC<FooterLocaleSwitcherProps> = props => {
  const [showDialog, setShowDialog] = React.useState(false);
  const open = useCallback(() => setShowDialog(true), []);
  const close = useCallback(() => setShowDialog(false), []);
  const switchLocale = (country: UrlLocaleEnum) => {
    const allSluglocales = props.slugLocales || [];
    const result = allSluglocales.find(element => localeToPath(element.locale) === country);
    return result ? `${result.slug}` : `/${country}/`;
  };

  return (
    <>
      <div>
        <OpenButton onClick={open} />
        <DialogOverlay isOpen={showDialog} onDismiss={close} className='fixed inset-0 bg-black/90 p-10 flex justify-center items-center z-10'>
          <CloseButton onClick={close} />
          <DialogContent aria-label='content' className='p-0 m-0 w-auto'>
            <>
              {supportedLocales.map(locale => (
                <SwitchItem key={locale} locale={locale} switchLocale={switchLocale} />
              ))}
            </>
          </DialogContent>
        </DialogOverlay>
      </div>
    </>
  );
};

const SwitchItem: React.FC<{locale: LocaleEnum; switchLocale: (country: UrlLocaleEnum) => string}> = ({locale, switchLocale}) => {
  const intl = useIntl();
  const localeAlias = localeToLocaleAlias(locale);
  return (
    <Link
      to={switchLocale(localeToPath(locale))}
      className={classNames('h-min text-[1.2em] text-primary-500 no-underline flex items-center p-7 border border-b-0 border-[rgb(229,233,235)]', {
        'bg-zinc-100': locale === intl.locale
      })}
    >
      <img src={`/images/flags/${FlagFileCodes[localeAlias]}.svg`} className='w-8 h-6 mr-[10px]' />
      <FormattedMessage id={`common.countries.${CountryIntlCodes[localeToLocaleAlias(locale)]}`} />
    </Link>
  );
};

const OpenButton: React.FC<{onClick: () => void}> = ({onClick}) => (
  <button aria-label='Open' className='bg-transparent border border-white rounded-full py-2 px-6 text-white cursor-pointer' onClick={onClick}>
    <FormattedMessage id='localeSwitcher.country' />
    <VisuallyHidden>Open</VisuallyHidden>
  </button>
);

const CloseButton: React.FC<{onClick: () => void}> = ({onClick}) => (
  <button
    aria-label='Close'
    onClick={onClick}
    className='text-white w-9 h-9 fixed right-0 top-0 cursor-pointer no-underline text-center p-0 text-3xl font-medium leading-9 text-shadow shadow-lg outline-none border-none bg-transparent'
  >
    <VisuallyHidden>Close</VisuallyHidden>
    <span aria-hidden>×</span>
  </button>
);
